import React, { useEffect, useState } from "react";
import { ChatCircleDots, UsersFour } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import MessagesThread from "@/components/MessagesThread";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data || []);
      if (!selected && data?.length) setSelected(data[0].link_id);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="messages-page">
      <div className="mb-8">
        <div className="label-tiny text-brand-accent mb-1">// COMMS</div>
        <h1 className="font-heading text-4xl sm:text-5xl">MESSAGES</h1>
        <p className="text-text-secondary text-sm mt-2">
          Direct 1:1 conversations with your coaches and athletes.
        </p>
      </div>

      {loading ? (
        <div className="label-tiny text-text-secondary">LOADING...</div>
      ) : threads.length === 0 ? (
        <div className="tactical-card p-10 text-center" data-testid="messages-empty-state">
          <ChatCircleDots size={40} weight="duotone" className="text-brand-accent mx-auto mb-3" />
          <p className="text-sm text-text-secondary">
            No active coaching relationships yet. Once you have a coach or client, threads will appear here.
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-px bg-white/10">
          {/* Thread list */}
          <div className="bg-surface-DEFAULT lg:col-span-1" data-testid="messages-thread-list">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <UsersFour size={14} weight="duotone" className="text-brand-accent" />
              <span className="label-tiny">CONVERSATIONS ({threads.length})</span>
            </div>
            <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
              {threads.map((t) => {
                const active = selected === t.link_id;
                const last = t.last_message;
                return (
                  <button
                    key={t.link_id}
                    onClick={() => setSelected(t.link_id)}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      active ? "bg-white/10" : "hover:bg-white/5"
                    }`}
                    data-testid={`thread-item-${t.link_id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {t.other?.picture ? (
                        <img src={t.other.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center text-xs font-bold">
                          {t.other?.name?.[0]?.toUpperCase() || "?"}
                        </div>
                      )}
                      <span className="font-heading text-sm flex-1 truncate inline-flex items-center gap-1">
                        {t.other?.name || "Unknown"}
                        {t.role !== "coach" && <VerifiedBadge verified={t.other?.is_verified} size="xs" />}
                      </span>
                      <span className="label-tiny text-brand-accent">{t.role === "coach" ? "CLIENT" : "COACH"}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-text-secondary truncate flex-1">
                        {last?.text
                          ? last.text
                          : last?.attachment
                            ? <em>[{(last.attachment.kind || "file").toUpperCase()}] {last.attachment.name || "Attachment"}</em>
                            : <em>No messages yet</em>}
                      </span>
                      {t.unread_count > 0 && (
                        <span
                          className="ml-2 bg-brand-accent text-surface-base text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums"
                          data-testid={`unread-badge-${t.link_id}`}
                        >
                          {t.unread_count}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message pane */}
          <div className="bg-surface-DEFAULT lg:col-span-2 p-0">
            {selected ? (
              <MessagesThread
                linkId={selected}
                currentUserId={user?.user_id}
                height="520px"
              />
            ) : (
              <div className="p-10 text-center text-sm text-text-secondary">
                Select a conversation to view messages.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
